/**
 * aC Men's Hub のコーデ生成ラン API contract（zod schema + 型）。
 * API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）が共有する。
 *
 * 1 回の生成依頼を「ラン」、その中の 1 コーデを「セット」と呼ぶ。画像生成は 1 本あたり
 * 十数秒かかるため、依頼は即座にランを作って返し、実際の生成は 1 セット = 1 タスクで
 * 非同期に進める。画面はランを見ればどこまで進んだかが分かる。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Shares the shape of a generation run and its individual outfits, so the Hub can show progress and approve results without the API and Web drifting apart.
 * @graph-connects none
 */
import { z } from 'zod';
import { CoordeOutfitItemSchema, CoordePreviewRequestSchema } from './coorde-outfit.js';
import { SeasonSchema } from './item-catalog.js';
/**
 * ランの状態。
 * `queued` は受け付けただけ、`running` は 1 本以上着手済み、`completed` は全セットが
 * 成否どちらかに確定した状態。`failed` はランそのものを開始できなかった場合に使う。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business The lifecycle of one generation request, so the screen can tell "not started yet" from "still working" from "everything settled".
 * @graph-connects none
 */
export const CoordeRunStatusSchema = z.enum(['queued', 'running', 'completed', 'failed']);
/**
 * セット（コーデ1本）の状態。
 * 画像が出来ていない `pending` / `failed` は承認の対象にならない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business The lifecycle of one outfit inside a run, separating "no image yet" from "image ready to judge".
 * @graph-connects none
 */
export const CoordeSetStatusSchema = z.enum(['pending', 'generated', 'failed']);
/**
 * 人の承認結果。**未承認の画像は書き出せない**（BR-5）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Records the human decision on one generated image, which is the gate that keeps unapproved material from being used.
 * @graph-connects none
 */
export const CoordeApprovalSchema = z.enum(['pending', 'approved', 'rejected']);
/**
 * 生成依頼。コーデ案の取得（`coorde.preview`）と同じ入力を使う。
 * 同じ条件で「案を見てから実行する」導線にするため、専用の型を別に作らない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business The generation request, deliberately identical to the preview request so a requester can preview and then run the very same conditions.
 * @graph-connects none
 */
export const CoordeRunRequestSchema = CoordePreviewRequestSchema;
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business One generated outfit: which garments it used, whether the image exists, and the human verdict on it.
 * @graph-connects none
 */
export const CoordeSetSchema = z.object({
    setId: z.string(),
    index: z.number(),
    items: z.array(CoordeOutfitItemSchema),
    status: CoordeSetStatusSchema,
    approval: CoordeApprovalSchema,
    /** 生成画像の GCS パス。未生成・失敗時は null。 */
    imagePath: z.string().nullable(),
    /** 失敗した理由。成功時は null。 */
    errorMessage: z.string().nullable(),
    /** 却下理由（任意入力）。学習のために残す。 */
    rejectReason: z.string().nullable(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business One generation request and everything produced by it, which is what the result screen renders.
 * @graph-connects none
 */
export const CoordeRunSchema = z.object({
    runId: z.string(),
    season: SeasonSchema,
    requestedCount: z.number(),
    includeOuter: z.boolean(),
    status: CoordeRunStatusSchema,
    requestedBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    sets: z.array(CoordeSetSchema),
});
/**
 * ランの受付レスポンス。生成はこの時点ではまだ終わっていない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Acknowledges a generation request and tells the caller how many outfits were actually queued, which can be fewer than requested when stock runs out.
 * @graph-connects none
 */
export const CoordeRunAcceptedSchema = z.object({
    runId: z.string(),
    requestedCount: z.number(),
    queuedCount: z.number(),
});
/** ラン1件を取得するリクエスト。 @graph-connects none */
export const CoordeRunQuerySchema = z.object({ runId: z.string().min(1) });
/**
 * セットの状態からランの状態を導く。
 *
 * ラン自身に進捗を書き込むのではなく、常にセットから計算する。ラン側にも状態を持つと
 * 「セットは全部終わっているのにランが running のまま」という食い違いが起きるため。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Derives the run status from its outfits so the run can never disagree with what actually happened.
 * @graph-connects none
 */
export function deriveRunStatus(sets) {
    if (sets.length === 0)
        return 'failed';
    if (sets.every((set) => set.status === 'pending'))
        return 'queued';
    if (sets.some((set) => set.status === 'pending'))
        return 'running';
    if (sets.every((set) => set.status === 'failed'))
        return 'failed';
    return 'completed';
}
/**
 * 書き出してよいセットだけを取り出す。
 * **生成に成功し、かつ人が承認したものだけ**が対象（BR-5）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Selects the outfits that may leave the Hub: generated successfully and explicitly approved by a person.
 * @graph-connects none
 */
export function selectExportableSets(sets) {
    return sets.filter((set) => set.status === 'generated' && set.approval === 'approved');
}
/**
 * 人が下せる判定。`pending` へ戻す導線は作らない（一度見たものを「未確認」には戻せない）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business The verdicts a reviewer can actually record, deliberately excluding "back to unreviewed".
 * @graph-connects none
 */
export const CoordeVerdictSchema = z.enum(['approved', 'rejected']);
/**
 * 承認・却下のリクエスト。
 *
 * 却下理由は任意。入れておくと後からプロンプト改善の材料になるので受け取れるようにするが、
 * 必須にすると「とりあえず承認」に流れるため必須にしない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Carries one human verdict on one generated image, with an optional reason kept for improving the prompt later.
 * @graph-connects none
 */
export const CoordeSetApprovalRequestSchema = z.object({
    runId: z.string().min(1),
    setId: z.string().min(1),
    approval: CoordeVerdictSchema,
    rejectReason: z.string().max(500).nullable(),
});
/**
 * 判定を下せるセットかどうか。
 *
 * 画像が出来ていないセット（未着手・生成失敗）は見るものが無いので判定できない。
 * 画面はボタンを出さないが、API 側でも同じ条件で弾く（画面だけの制限にしない）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Allows a verdict only on outfits that actually produced an image, so approval always refers to something a person could see.
 * @graph-connects none
 */
export function canJudgeSet(set) {
    return set.status === 'generated';
}
/**
 * ラン1件の要約。履歴一覧はセットの中身まで要らないので、件数だけに畳んで返す。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Condenses a run into the counts a history list needs, so the list does not carry every outfit's full detail.
 * @graph-connects none
 */
export const CoordeRunSummarySchema = z.object({
    runId: z.string(),
    season: SeasonSchema,
    requestedCount: z.number(),
    includeOuter: z.boolean(),
    status: CoordeRunStatusSchema,
    requestedBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** 実際に組めた本数。在庫不足だと requestedCount より少ない。 */
    setCount: z.number(),
    /** 画像が出来た本数。 */
    generatedCount: z.number(),
    /** 生成に失敗した本数。 */
    failedCount: z.number(),
    /** 承認済みの本数。 */
    approvedCount: z.number(),
    /** まだ judge していない本数（画像が出来ているもののうち）。 */
    unjudgedCount: z.number(),
});
/** 履歴一覧のレスポンス。 @graph-connects none */
export const CoordeRunListSchema = z.object({ runs: z.array(CoordeRunSummarySchema) });
/**
 * ランを履歴一覧用に畳む。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Turns a run into the counts the history list shows, including how many results still need a human decision.
 * @graph-connects none
 */
export function summarizeRun(run) {
    const generated = run.sets.filter((set) => set.status === 'generated');
    return {
        runId: run.runId,
        season: run.season,
        requestedCount: run.requestedCount,
        includeOuter: run.includeOuter,
        status: run.status,
        requestedBy: run.requestedBy,
        createdAt: run.createdAt,
        updatedAt: run.updatedAt,
        setCount: run.sets.length,
        generatedCount: generated.length,
        failedCount: run.sets.filter((set) => set.status === 'failed').length,
        approvedCount: generated.filter((set) => set.approval === 'approved').length,
        unjudgedCount: generated.filter((set) => set.approval === 'pending').length,
    };
}
//# sourceMappingURL=coorde-run.js.map